import React, { useEffect, useState, useRef } from 'react';
import cn from 'classnames';
import { useIntl } from 'react-intl';
import { Icons } from '..';
import { applyClasses, useKeyPress, constants } from '../shared/js';

import styles from './nav.module.scss';

const { ESCAPE_KEY } = constants;

const apiSearchResults = [
  {
    title: 'How your teen can ace their driving test',
    url: 'https://www.sonnet.ca/blog/auto/how-your-teen-can-ace-their-driving-test',
  },
  {
    title:
      'Sonnet vs. the other guys: Putting the online insurance experience to the test',
    url: 'https://www.sonnet.ca/blog/lifestyle/sonnet-vs-the-other-guys',
  },
  {
    title: 'Tips on buying a vehicle with the greatest resale value',
    url: 'https://www.sonnet.ca/blog/auto/general/best-resale-value-cars',
  },
  {
    title: 'I have questions about my pet insurance coverage. Can you help?',
    url: 'https://www.sonnet.ca/faqs/policy-coverages/pet-insurance-coverage',
  },
  {
    title: '10 common house fire causes - and how to prevent them',
    url: 'https://www.sonnet.ca/blog/home/10-common-house-fire-causes-and-how-to-prevent-them',
  },
  {
    title: 'How to reduce risk with smart home systems',
    url: 'https://www.sonnet.ca/blog/home/insurance/how-to-reduce-risk-with-smart-home-systems',
  },
  {
    title: 'Unique insurance needs for condos: re-sale vs. new build',
    url: 'https://www.sonnet.ca/blog/home/insurance/unique-insurance-needs-condo',
  },
  {
    title: 'The ultimate guide to preparing your property for the winter',
    url: 'https://www.sonnet.ca/blog/home/ultimate-guide-to-preparing-your-property-for-winter',
  },
  {
    title: 'Sonnet Insurance - Is the glass half empty, or half full?',
    url: 'https://www.sonnet.ca/blog/lifestyle/glass-half-full',
  },
  {
    title: 'How to choose the best moving company',
    url: 'https://www.sonnet.ca/blog/home/general/choose-best-moving-company',
  },
  {
    title: 'This result should not be shown',
    url: '#not-shown',
  },
  {
    title: 'This should not be shown either',
    url: '#not-shown2',
  },
];

const NavSearch = (props) => {
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResultsOpen, setSearchResultsOpen] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const { formatMessage } = useIntl();
  const inputRef = useRef();
  const escPressed = useKeyPress(ESCAPE_KEY);

  const fetchSearchResults = (query) => {
    // should query an API
    return query ? apiSearchResults : [];
  };

  useEffect(() => {
    if (searchOpen) {
      inputRef.current.focus();
    } else {
      inputRef.current.value = '';
    }
  }, [searchOpen]);

  useEffect(() => {
    if (!escPressed) return;
    if (searchResultsOpen) {
      setSearchResultsOpen(false);
    } else if (searchOpen) {
      setSearchOpen(false);
    }
  }, [escPressed]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setSearchResults(fetchSearchResults(searchQuery));
  }, [searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  const searchToggleLabel = formatMessage({
    id: 'nav.search.open',
    defaultMessage: 'Open the search bar',
    description: 'Label to describe the behaviour of the search button',
  });
  const searchBarLabel = formatMessage({
    id: 'nav.search.label',
    defaultMessage: 'Search query',
    description: 'Label to describe the search input',
  });
  const searchBarPlaceholder = formatMessage({
    id: 'nav.search.placeholder',
    defaultMessage: 'Search',
    description: 'Placeholder for the search input',
  });
  const searchBarClose = formatMessage({
    id: 'nav.search.close',
    defaultMessage: 'Close the search bar',
    description: 'Label to describe the behaviour of the close search button',
  });

  const handleSearch = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
  };

  return (
    <>
      <button
        className={cn(styles.search, applyClasses(props, styles))}
        onClick={() => setSearchOpen(!searchOpen)}
        aria-label={searchToggleLabel}
      >
        <span className={styles.iconContainer}>
          <Icons.Search />
        </span>
        Search
      </button>
      <div
        className={cn(styles.searchBarContainer, {
          [styles.searchOpen]: searchOpen,
        })}
      >
        <div className={styles.searchBarInner}>
          <button
            className={styles.searchBack}
            onClick={() => setSearchOpen(false)}
            aria-label={searchBarClose}
          >
            <div className={cn(styles.closeIcon, styles.chevron)}>
              <Icons.Chevron />
            </div>
            Back
          </button>
          <div className={styles.inputWrapper}>
            <span className={styles.searchBarIcon}>
              <Icons.Search />
            </span>
            <input
              ref={inputRef}
              value={searchQuery}
              type="text"
              className={cn(styles.searchBar, {
                [styles.showingResults]: searchResults.length,
              })}
              aria-label={searchBarLabel}
              placeholder={searchBarPlaceholder}
              onChange={handleSearch}
            />
            {searchResults.length > 0 && (
              <ul className={styles.searchResults}>
                {searchResults.map((searchResult, index) => (
                  <li key={index} className={styles.searchResult}>
                    <a href={searchResult.url}>{searchResult.title}</a>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <button
            className={styles.searchClose}
            onClick={() => setSearchOpen(false)}
            aria-label={searchBarClose}
          >
            <div className={styles.closeIcon}>
              <Icons.Close />
            </div>
          </button>
        </div>
        <div
          className={styles.searchOverlay}
          onClick={() => setSearchOpen(false)}
        />
      </div>
    </>
  );
};

export default NavSearch;
